import { map, switchMap, Observable, of, tap, take } from 'rxjs';

import {
  C_Save_Operation_Status,
  GetConfigDataQuery,
  SaveConfigDataMutation,
  Wa_SaveConfigInput,
} from '../../../graphql/generatedModel';
import { gqlClient } from '../../../graphql/graphqlRequest';
import { storageHelper } from '../../helpers/storage';
import { sessionstorage } from '../../helpers/storage/session.keys';
import { getCompanyConfig, saveConfigData } from './gql/getConfigs.gql';
import { responseHandler } from '../../responseHandler/responseHandler';
import { localeFormatterHelper } from '../../helpers/formatter/localeFormatter.helper.ts';
class CompanyConfig {
  private storageKey: sessionstorage = 'companyConfig';
  private storageConfig: { data?: TCompanyConfigs; updatedAt?: number } | undefined =
    storageHelper.session.getItem(this.storageKey);

  async getConfigsRaw(forceRun?: boolean): Promise<TCompanyConfigs> {
    return new Promise((res) => {
      this.getConfigs(forceRun)
        .pipe(take(1))
        .subscribe((v) => res(v));
    });
  }
  getConfigs(forceRun?: boolean): Observable<TCompanyConfigs> {
    // Update session storage value if it was last updated not today
    const isActualCacheData =
      localeFormatterHelper.localizedDate().getDate() ===
      localeFormatterHelper.localizedDate(this.storageConfig?.updatedAt ?? 0).getDate();
    const useCache = this.storageConfig?.data && !forceRun && isActualCacheData;

    return useCache
      ? of(this.storageConfig!.data!)
      : gqlClient(getCompanyConfig).pipe(
          map((res) => {
            const typedRes = res as GetConfigDataQuery;
            this.storageConfig = {
              data: typedRes?.wawiAssist?.getConfigData,
              updatedAt: localeFormatterHelper.localizedDate().getTime(),
            };
            storageHelper.session.setItem(this.storageKey, this.storageConfig);
            return this.storageConfig.data as TCompanyConfigs;
          }),
          responseHandler<TCompanyConfigs>({
            errorReturnType: undefined,
            customErrorHandler: (e) => {
              // A genuine session expiry is a login-lifecycle event, not a bug:
              // gqlClient has already flagged it and redirected to login. Do NOT
              // re-throw here — a fresh Error would escape uncaught WITHOUT the
              // __sentryIgnore/__sessionExpired flags gqlClient set, bypassing
              // both responseHandler's captureMessage guard and main.tsx's
              // beforeSend, which auto-files Jira tickets (WA-3598 — same family
              // as WA-3514…WA-3538 / WA-3546). Returning here lets the stream
              // emit errorReturnType (undefined) so getConfigsRaw's promise
              // resolves instead of hanging on an unhandled error.
              if (e.__sessionExpired) return;
              throw new Error(`Error in getConfigData request: ${e.message}`);
            },
          }),
        );
  }
  getCachedConfigs(): TCompanyConfigs | undefined {
    return this.storageConfig?.data ? { ...this.storageConfig?.data } : undefined;
  }
  setShowDoughInLiterInProductionLists(v: boolean): void {
    this.storageConfig = {
      ...(this.storageConfig || {}),
      data: {
        ...(this.storageConfig?.data || ({} as TCompanyConfigs)),
        isDoughInLitersInProductionReports: v,
      },
    };
    storageHelper.session.setItem(this.storageKey, this.storageConfig);
  }
  saveConfigData(params: Wa_SaveConfigInput): Observable<any> {
    return (
      gqlClient(saveConfigData, { dataToSave: params }) as Observable<SaveConfigDataMutation>
    ).pipe(
      map((res) => res.wawiAssist?.saveConfigData as TSaveConfigData),
      responseHandler<TSaveConfigData>({
        success: () => 'settings.configuration_saved',
        customErrorHandler: () => 'settings.configuration_not_saved',
        errorReturnType: {
          status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED,
        },
      }),
      switchMap((res) => {
        const out = {
          updateStorageValue: res.status === C_Save_Operation_Status.SOS1_DATA_CHANGED,
        };
        // Update of fields isDeliveryOn... require fetch new value for nextDeliveryDate field
        const fieldsRequireReFetch = [
          'isDeliveryOnMon',
          'isDeliveryOnTue',
          'isDeliveryOnWed',
          'isDeliveryOnThu',
          'isDeliveryOnFri',
          'isDeliveryOnSat',
          'isDeliveryOnSun',
        ];
        const isFetchRequired = Object.keys(params).some((field) =>
          fieldsRequireReFetch.includes(field),
        );
        if (isFetchRequired) {
          return this.getConfigs(true).pipe(
            map(() => {
              // storage value is updated at this.getConfigs method
              out.updateStorageValue = false;
              return out;
            }),
          );
        }

        return of(out);
      }),
      tap((res) => {
        if (res.updateStorageValue) {
          this.storageConfig = {
            ...(this.storageConfig ?? {}),
            data: {
              ...(this.storageConfig?.data ?? {}),
              ...(params as TCompanyConfigs),
            },
          };
          storageHelper.session.setItem(this.storageKey, this.storageConfig);
        }
      }),
    );
  }
}

export const companyConfigService = new CompanyConfig();

export type TCompanyConfigs = NonNullable<
  NonNullable<GetConfigDataQuery['wawiAssist']>['getConfigData']
>;

export type TSaveConfigData = NonNullable<
  NonNullable<SaveConfigDataMutation['wawiAssist']>['saveConfigData']
>;
